import React, { useState } from 'react';
import css from './NumberedSnippet.scss';
import { TextButton, Text, Divider } from '@wix/design-system';
import { Visible, Hidden } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useTranslation } from '@wix/wix-i18n-config';
import type { AppDetails } from '../../types';
import { CopyCodeToClipboard } from '../../../../../UtilityComponents/CopyCodeToClipboard';
import { getSelfHostedAppText, SelfHostedAppCode } from './SelfHostedAppCode';
import { useTemplatesData } from '../../../../../../hooks/useTemplatesData';
import { DataHooks } from '../../../../../../constants/dataHooks';
import { useTemplatesPageBi } from '../../../../../../hooks/useTemplatesPageBi';

interface NumberedSnippetProps extends AppDetails {
  headerText: string;
  className?: string;
}

export const NumberedSnippet = ({
  headerText,
  appId,
  appSecret,
  appPublicKey,
  className,
}: NumberedSnippetProps) => {
  const [isSecretShown, setIsSecretShown] = useState(false);
  const { t } = useTranslation();
  const { currentTemplate: template } = useTemplatesData();
  const { reportToggleSecretsClick } = useTemplatesPageBi();

  const onToggleSecretsClick = () => {
    reportToggleSecretsClick(template.id, isSecretShown);
    setIsSecretShown((prev) => !prev);
  };

  return (
    <div
      data-hook={DataHooks.SelfHostedModalSnippet}
      className={classNames(css.base, className)}
    >
      <div className={css.header}>
        <Text className={css.name} size="small">
          {headerText}
        </Text>
        <div className={css.actions}>
          <TextButton
            dataHook="display-secrets-toggle"
            skin="light"
            size="small"
            onClick={onToggleSecretsClick}
            suffixIcon={isSecretShown ? <Hidden /> : <Visible />}
          >
            {isSecretShown
              ? t('app.template-page.modal.self-hosted.step3.secret-hidden')
              : t('app.template-page.modal.self-hosted.step3.secret-shown')}
          </TextButton>
          <Divider skin="light" direction="vertical" className={css.divider} />
          <CopyCodeToClipboard
            origin="self-hosted-modal-snippet"
            valueToCopy={getSelfHostedAppText({
              appId,
              appSecret,
              appPublicKey,
            })}
            skin="light"
          />
        </div>
      </div>
      <div className={css.content}>
        <SideNumbers />
        <SelfHostedAppCode
          appId={appId}
          appSecret={isSecretShown ? appSecret : '*****************'}
          appPublicKey={appPublicKey}
        />
      </div>
    </div>
  );
};

const SideNumbers = () => (
  <div className={css.numbers}>
    {Array(9)
      .fill(true)
      .map((_, index) => (
        <div>{index + 1}</div>
      ))}
  </div>
);
